import { CompanyRegistryCombobox, type CompanyRegistrySelection } from '@/components/ui/company-registry-combobox';
import {
    ResponsiveModal,
    ResponsiveModalBody,
    ResponsiveModalContent,
    ResponsiveModalDescription,
    ResponsiveModalFooter,
    ResponsiveModalHeader,
    ResponsiveModalTitle,
    ResponsiveModalTrigger,
} from '@/components/ui/responsive-modal';
import { Button } from '@/components/ui/shadcn/button';
import { Label } from '@/components/ui/shadcn/label';
import { useControllableOpen } from '@/hooks/use-controllable-open';
import { useTranslations } from '@/hooks/useTranslations';
import { router } from '@inertiajs/react';
import { AlertTriangle, ArrowRight } from 'lucide-react';
import { type ReactNode, useState } from 'react';
import { toast } from 'sonner';
import { route } from 'ziggy-js';

interface IdentityCompany {
    id: string;
    name: string;
    company_code?: string | null;
}

interface CorrectCompanyIdentityModalProps {
    company: IdentityCompany;
    trigger?: ReactNode;
    /** Set to drive the modal from the page — see useControllableOpen. */
    open?: boolean;
    onOpenChange?: (open: boolean) => void;
}

export default function CorrectCompanyIdentityModal({ company, trigger, open: openProp, onOpenChange }: CorrectCompanyIdentityModalProps) {
    const { t } = useTranslations();
    const [open, setOpen] = useControllableOpen(openProp, onOpenChange);
    const [selection, setSelection] = useState<{ name: string; code: string } | null>(null);
    const [processing, setProcessing] = useState(false);

    const reset = () => {
        setSelection(null);
        setProcessing(false);
    };

    /**
     * Only a real registry entry counts. The combobox also emits a `manual` mode for
     * whatever the user typed, which is exactly what must not become an identity.
     */
    const handleSelection = (next: CompanyRegistrySelection | null) => {
        if (!next || next.mode === 'manual') {
            setSelection(null);
            return;
        }

        setSelection({ name: next.details.name ?? '', code: next.details.code ?? '' });
    };

    const handleOpenChange = (next: boolean) => {
        if (!next) reset();
        setOpen(next);
    };

    const handleSubmit = () => {
        if (!selection?.code) return;

        setProcessing(true);

        router.post(
            route('companies.identity.update', { company: company.id }),
            { registry_code: selection.code },
            {
                preserveScroll: true,
                onSuccess: () => {
                    setOpen(false);
                    reset();
                },
                onError: (errors) => {
                    toast.error(Object.values(errors)[0] ?? t('common.error', 'Something went wrong.'));
                },
                onFinish: () => setProcessing(false),
            },
        );
    };

    const changesName = selection !== null && selection.name !== company.name;
    const changesCode = selection !== null && selection.code !== (company.company_code ?? '');
    const hasChange = changesName || changesCode;

    return (
        <ResponsiveModal open={open} onOpenChange={handleOpenChange} variant="sheet">
            {trigger ? <ResponsiveModalTrigger asChild>{trigger}</ResponsiveModalTrigger> : null}
            <ResponsiveModalContent size="md">
                <ResponsiveModalHeader>
                    <ResponsiveModalTitle>{t('companies.identity.title', 'Correct company identity')}</ResponsiveModalTitle>
                    <ResponsiveModalDescription>
                        {t(
                            'companies.identity.description',
                            'Replace a wrong name or registration code with the official registry entry. The previous identity is kept as an alias so old records still resolve.',
                        )}
                    </ResponsiveModalDescription>
                </ResponsiveModalHeader>

                <ResponsiveModalBody className="space-y-5">
                    <div className="space-y-1.5">
                        <Label>{t('companies.identity.current', 'Current identity')}</Label>
                        <div className="rounded-md border px-3 py-2.5">
                            <p className="text-sm font-medium">{company.name}</p>
                            <p className="text-muted-foreground text-xs tabular-nums">
                                {company.company_code || t('companies.identity.no_code', 'No code')}
                            </p>
                        </div>
                    </div>

                    <div className="space-y-1.5">
                        <Label>{t('companies.identity.search_label', 'Official registry entry')}</Label>
                        <CompanyRegistryCombobox
                            onSelectionChange={handleSelection}
                            placeholder={t('companies.identity.search_placeholder', 'Search the registry…')}
                            searchPlaceholder={t('companies.forms.add.placeholders.search_company', 'Enter company name or code')}
                            emptyText={t('companies.forms.add.placeholders.no_companies', 'No companies found')}
                            existingBadgeText={t('companies.forms.add.badges.existing', 'Existing')}
                        />
                        <p className="text-muted-foreground text-xs">
                            {t('companies.identity.hint', 'Name and code can only come from the registry — they cannot be typed by hand.')}
                        </p>
                    </div>

                    {selection && hasChange ? (
                        <div className="space-y-1.5">
                            <Label>{t('companies.identity.result', 'After saving')}</Label>
                            <div className="space-y-2 rounded-md border px-3 py-2.5 text-sm">
                                {changesName ? (
                                    <div className="flex flex-wrap items-center gap-2">
                                        <span className="text-muted-foreground line-through">{company.name}</span>
                                        <ArrowRight className="text-muted-foreground h-3.5 w-3.5 shrink-0" />
                                        <span className="font-medium">{selection.name}</span>
                                    </div>
                                ) : null}
                                {changesCode ? (
                                    <div className="flex flex-wrap items-center gap-2 tabular-nums">
                                        <span className="text-muted-foreground line-through">{company.company_code}</span>
                                        <ArrowRight className="text-muted-foreground h-3.5 w-3.5 shrink-0" />
                                        <span className="font-medium">{selection.code}</span>
                                    </div>
                                ) : null}
                            </div>
                        </div>
                    ) : null}

                    {selection && !hasChange ? (
                        <p className="text-muted-foreground text-sm">
                            {t('companies.identity.already_matches', 'This company already matches the selected registry entry.')}
                        </p>
                    ) : null}

                    <div className="bg-warning-subtle text-warning-subtle-foreground flex gap-2 rounded-md px-3 py-2.5 text-sm">
                        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                        <span>
                            {t(
                                'companies.identity.warning',
                                'This renames the company everywhere it appears. Only do it when the stored identity is genuinely wrong.',
                            )}
                        </span>
                    </div>
                </ResponsiveModalBody>

                <ResponsiveModalFooter>
                    <Button variant="outline" onClick={() => handleOpenChange(false)} disabled={processing}>
                        {t('companies.identity.cancel', 'Cancel')}
                    </Button>
                    <Button onClick={handleSubmit} disabled={processing || !hasChange}>
                        {processing ? t('common.saving', 'Saving...') : t('companies.identity.confirm', 'Apply registry identity')}
                    </Button>
                </ResponsiveModalFooter>
            </ResponsiveModalContent>
        </ResponsiveModal>
    );
}
